import React, { useEffect, useState } from "react";
import { X } from "react-feather";
import { apiService } from "../../../../services/apiService.ts";
import InvoiceModal from "./InvoiceModal.jsx";
import StaffPinModal from "../Common/StaffPinModal.jsx";

export default function Departures({
  isOpen,
  onClose,
  booking,
  initialAccount,
}) {
  const [, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [guests, setGuests] = useState([]);
  const [selectedGuestIds, setSelectedGuestIds] = useState([]);

  const [isLoadingGuests, setIsLoadingGuests] = useState(false);

  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [currentStaffId, setCurrentStaffId] = useState(null);

  const [completedModalOpen, setCompletedModalOpen] = useState(false);
  const [responsibleWarningOpen, setResponsibleWarningOpen] =
    useState(false);

  const [staffPinModal, setStaffPinModal] = useState(false);
  const [, setIsSubmitting] = useState(false);

  // Load accounts for booking
  useEffect(() => {
    if (!isOpen || !booking?.BookingHeaderID) return;

    apiService
      .getBookingAccounts(booking.BookingHeaderID)
      .then((res) => {
        if (res.Success) setAccounts(res.Data);
      });
  }, [isOpen, booking]);

  // Pre-select account
  useEffect(() => {
    if (!isOpen) return;

    if (initialAccount) {
      setSelectedAccount(initialAccount);
    } else {
      setSelectedAccount(null);
    }

    setSelectedGuestIds([]);
    setGuests([]);
  }, [isOpen, initialAccount]);

  // Load guests for selected account
  useEffect(() => {
    if (!isOpen || !selectedAccount) return;

    setIsLoadingGuests(true);
    apiService
      .getAccountGuests(String(selectedAccount.AccountID))
      .then((res) => {
        if (res.Success && Array.isArray(res.Data)) {
          setGuests(res.Data);
        } else {
          setGuests([]);
        }
        setIsLoadingGuests(false);
      })
      .catch(() => setIsLoadingGuests(false));
  }, [isOpen, selectedAccount]);

  const toggleGuest = (guestId) => {
    setSelectedGuestIds((prev) =>
      prev.includes(guestId)
        ? prev.filter((x) => x !== guestId)
        : [...prev, guestId]
    );
  };

  const handleCheckOut = () => {
    let selected = [...selectedGuestIds];
    if (selected.length === 0) {
      selected = guests.map((g) => g.GuestID);
      setSelectedGuestIds(selected);
    }

    if (!selectedAccount) return;

    // Hide current modal
const mainModal = document.querySelector(".pos-modal");
if (mainModal) mainModal.style.display = "none";

setStaffPinModal(true);
  };

  // Submit checkout after PIN
  const handleSubmitCheckoutWithStaff = async (staffId) => {
    try {
      setIsSubmitting(true);

      const checkoutIds =
        selectedGuestIds.length > 0
          ? selectedGuestIds
          : guests.map((g) => g.GuestID);

      const selectedGuests = guests.filter((g) =>
        checkoutIds.includes(g.GuestID)
      );

      const nonResponsibleIds = selectedGuests
        .filter((g) => !g.IsResponsible)
        .map((g) => g.GuestID);

      const responsibleGuest =
        selectedGuests.find((g) => g.IsResponsible) || null;

      // PARTIAL CHECKOUT
      if (nonResponsibleIds.length > 0) {
        await apiService.partialCheckout(
          String(selectedAccount.AccountID),
          selectedAccount?.IsSettled || false,
          nonResponsibleIds,
          staffId
        );
      }

      // RESPONSIBLE GUEST → invoice
      if (responsibleGuest) {
        try {
          const res = await apiService.generateAccountInvoice(
            String(selectedAccount.AccountID)
          );

          if (!res.Success || !res.Data) {
            setResponsibleWarningOpen(true);
            return;
          }

          const data = res.Data;

          const excl = Number(data.InvoiceTotalExcl || 0);
          const vat = Number(data.InvoiceTotalVat || 0);
          const incl =
            typeof data.InvoiceTotalIncl === "object"
              ? Number(data.InvoiceTotalIncl.parsedValue || 0)
              : Number(data.InvoiceTotalIncl || 0);

          const items = [];
          if (Array.isArray(data.Tabs)) {
            data.Tabs.forEach((tab) => {
              if (Array.isArray(tab.TabLines)) {
                tab.TabLines.forEach((line) => {
                  const amount =
                    typeof line.LineTotalIncl === "object"
                      ? Number(line.LineTotalIncl.parsedValue || 0)
                      : Number(line.LineTotalIncl || 0);

                  items.push({
                    date: new Date(
                      tab.ClosedDate || Date.now()
                    ).toLocaleDateString(),
                    description: line.Product || "Item",
                    amount,
                  });
                });
              }
            });
          }

          setInvoiceData({
            company: "Blue Safari Seychelles",
            phone: "+248 422 97 00",
            email: "reservations@bluesafari.com",
            guestName:
              responsibleGuest.FirstName +
              " " +
              responsibleGuest.LastName,
            address: "P.O Box 378, Mahé, Seychelles",
            bookingInfo: {
              confNo: data.BookingReference || "—",
              crsNo: data.AccountName || "—",
              date: new Date().toLocaleDateString(),
              time: new Date().toLocaleTimeString(),
            },
            stayInfo: {
              guests: guests.length,
              arrival: new Date().toLocaleDateString(),
              departure: new Date().toLocaleDateString(),
              roomNo: "{21}",
              roomType: "{AILBB}",
              cashier: "{SYSTEM}",
            },
            items,
            balance: excl,
            vat,
            eqBalanceEur: incl / 16.4,
            eqBalanceUsd: incl / 14.1,
          });

          setInvoiceModalOpen(true);
          setCurrentStaffId(staffId);
        } catch (err) {
          setResponsibleWarningOpen(true);
        }
      } else {
        setCompletedModalOpen(true);
      }

      setStaffPinModal(false);
    } catch (err) {
      console.error("Checkout failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* MAIN MODAL */}
      <div
        className="modal fade show d-block pos-modal"
        style={{ background: "rgba(0,0,0,0.45)", zIndex: 30000 }}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content rounded-4 shadow-lg">
            <div className="modal-header border-0 pb-2">
              <h5 className="modal-title fw-semibold">
                Check-Out — {selectedAccount?.Name || "Select Account"}
              </h5>
              <button className="btn btn-light" onClick={onClose}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              <label className="fw-semibold mb-1">Account</label>
              <input
                readOnly
                type="text"
                className="form-control mb-3 bg-light"
                value={selectedAccount?.Name || ""}
                placeholder="Select account from table"
              />

              <h6 className="fw-semibold mb-2">
                Select Guests for Partial Check-Out
              </h6>

              <div
                className="table-responsive border rounded"
                style={{ maxHeight: "350px" }}
              >
                <table className="table table-sm table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Select</th>
                      <th>Name</th>
                      <th>Responsible</th>
                    </tr>
                  </thead>

                  <tbody>
                    {isLoadingGuests && (
                      <tr>
                        <td
                          colSpan="3"
                          className="text-center text-muted py-3"
                        >
                          Loading guests…
                        </td>
                      </tr>
                    )}

                    {!isLoadingGuests &&
                      guests.map((g) => (
                        <tr key={g.GuestID}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedGuestIds.includes(
                                g.GuestID
                              )}
                              onChange={() => toggleGuest(g.GuestID)}
                            />
                          </td>
                          <td>
                            {g.FirstName} {g.LastName}
                          </td>
                          <td>
                            {g.IsResponsible ? "✔️" : "-"}
                          </td>
                        </tr>
                      ))}

                    {!isLoadingGuests &&
                      guests.length === 0 && (
                        <tr>
                          <td
                            colSpan="3"
                            className="text-center py-3 text-muted"
                          >
                            No guests found.
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer border-0 pt-0">
              <button
                className="btn btn-primary px-4"
                disabled={!selectedAccount}
                onClick={handleCheckOut}
              >
                Check Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* RESPONSIBLE WARNING */}
      {responsibleWarningOpen && (
        <div
          className="modal fade show d-block pos-modal"
          style={{
            background: "rgba(0,0,0,0.45)",
            zIndex: 32000,
          }}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content rounded-4 shadow-lg">
              <div className="modal-header border-0 pb-2">
                <h5 className="modal-title fw-semibold">
                  Cannot Check Out
                </h5>
                <button
                  className="btn btn-light"
                  onClick={() => setResponsibleWarningOpen(false)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body">
                <p className="text-danger fw-semibold mb-0">
                  Responsible guest cannot be checked out until
                  invoice is settled.
                </p>
              </div>

              <div className="modal-footer border-0 pt-0">
                <button
                  className="btn btn-primary"
                  onClick={() =>
                    setResponsibleWarningOpen(false)
                  }
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* COMPLETED MODAL */}
      {completedModalOpen && (
        <div
          className="modal fade show d-block pos-modal"
          style={{
            background: "rgba(0,0,0,0.45)",
            zIndex: 33000,
          }}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content rounded-4 shadow-lg">
              <div className="modal-header border-0 pb-2">
                <h5 className="modal-title fw-semibold">
                  Completed
                </h5>
                <button
                  className="btn btn-light"
                  onClick={() =>
                    setCompletedModalOpen(false)
                  }
                >
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body text-center">
                <p>Guest(s) checked out successfully.</p>
              </div>

              <div className="modal-footer border-0 pt-0">
                <button
                  className="btn btn-primary px-4"
                  onClick={() => setCompletedModalOpen(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* INVOICE MODAL */}
      {invoiceModalOpen && invoiceData && (
        <InvoiceModal
          isOpen={invoiceModalOpen}
          onClose={() => setInvoiceModalOpen(false)}
          invoiceData={invoiceData}
          accountId={String(selectedAccount?.AccountID)}
          staffId={currentStaffId}
          onContinue={async (paymentTypeId) => {
            await apiService.fullCheckout(
              String(selectedAccount.AccountID),
              currentStaffId,
              paymentTypeId
            );
            setInvoiceModalOpen(false);
            setCompletedModalOpen(true);
          }}
        />
      )}

      {/* NEW STAFF PIN MODAL */}
      <StaffPinModal
        isOpen={staffPinModal}
        onClose={() => setStaffPinModal(false)}
        onApproved={(staff) => {
    const staffId = staff.StaffID;

    // Restore modal display so it can close normally
    const mainModal = document.querySelector(".pos-modal");
    if (mainModal) mainModal.style.display = "";

    handleSubmitCheckoutWithStaff(staffId);
}}

      />
    </>
  );
}
